import { relayDateLabel } from "./bullseyeRelaySheet";
import { isTwentyTwoEntrant } from "../Leaderboard/bullseyeResultGrouping";

const text = value => String(value ?? "").trim();
const id = value => text(value).toLowerCase();
const number = value => value === null || value === undefined || text(value) === "" ? null
  : Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : null;
const profile = row => ({ ...row, ...row?.disciplineProfile });
const membership = (row, system) => (row.membershipSnapshot || []).find(item => id(item.system) === system)?.memberId;

// This generic app label is not useful on an NRA reporting card. Keep actual
// course/program descriptions without changing the match's stored sanction.
export function nraReportTournamentType(...values) {
  return values.flatMap(value => text(value).split(/\s+[-\u2013\u2014|\u00b7]\s+/))
    .map(text).filter(value => value && !/^club\s+sanctioned\s+tournament$/i.test(value)).join(" - ");
}

export function nraReportHeader(match) {
  const environment = /outdoor/i.test(match.environment || match.selectedRangeFormat || "") ? "Outdoors"
    : /indoor/i.test(match.environment || match.selectedRangeFormat || "") ? "Indoors" : "";
  return {
    discipline: ["Conventional Pistol", environment].filter(Boolean).join(" - "),
    date: relayDateLabel(match),
    location: match.rangeName || match.location || [match.city, match.state].filter(Boolean).join(", "),
    type: nraReportTournamentType(match.selectedCourseTemplateName, match.sanctionedType),
    director: match.matchDirectorName || match.directorName || "",
  };
}

function reportName(person, fallback) {
  const first = [person.firstName, person.middleNameOrInitial].map(text).filter(Boolean).join(" ");
  const last = [person.lastName, person.suffix].map(text).filter(Boolean).join(" ");
  // Do not guess surnames for compound names when only a display name exists.
  return last ? [last, first].filter(Boolean).join(", ") : text(person.displayName || fallback);
}

function participation(person, score) {
  const aggregate = id(person.aggregateParticipation);
  if (/^(?:twenty_two_only|twentytwoonly|twenty_two_only_aggregate|22_only|22only)$/.test(aggregate)
    || isTwentyTwoEntrant(person) || (!aggregate && isTwentyTwoEntrant(score))) return "22 Only";
  const division = text(person.division || score.division);
  return /^metallic$/i.test(division) ? "Metallic" : /^open$/i.test(division) ? "Open" : division;
}

// Match aggregates are already summed by the scoring API. Never add firearm rows,
// stage rows or result snapshots again, and never join private profiles by name.
export function buildNraReport({ registrations = [], participants = [], entries = [], scores = [] }) {
  const participantById = new Map(participants.map(row => [id(row.participantId), row]));
  const registrationById = new Map(registrations.map(row => [id(row.registrationId), row]));
  const entryById = new Map(entries.map(row => [id(row.entryId), row]));
  const errors = [], rows = [], seen = new Set();
  let unscored = 0;
  for (const score of scores) {
    const shots = number(score.shotsFired);
    const total = number(score.totalScore);
    const xs = number(score.xCount);
    const name = text(score.competitorName || score.displayName) || "Unnamed competitor";
    if (shots === 0 && !total && !xs) { unscored += 1; continue; }
    if (shots === null && !total && !xs && /^(?:unscored|pending|registered)?$/.test(score.status || "")) { unscored += 1; continue; }
    if (shots === null || shots === 0 || !Number.isInteger(shots) || total === null || xs === null
      || !Number.isInteger(total) || !Number.isInteger(xs) || total > shots * 10 || xs > shots || xs * 10 > total) {
      errors.push(`${name}: actual shots, total score or X count is missing or invalid.`); continue;
    }
    const entry = entryById.get(id(score.canonicalEntryId || score.entryId));
    let participant = participantById.get(id(score.participantId || entry?.participantId));
    // Older public aggregates expose the local adapter ID. Only an explicit
    // canonical participant link is accepted, never a name/email match.
    if (!participant && !score.participantId && !entry) {
      const linked = participants.filter(row => id(row.legacyCompetitorId) && id(row.legacyCompetitorId) === id(score.competitorId));
      if (linked.length === 1) participant = linked[0];
    }
    const key = id(participant?.participantId || score.participantId || entry?.participantId || score.competitorId);
    if (!key || seen.has(key)) { errors.push(`${name}: duplicate or missing participant identity. Refresh the report after syncing.`); continue; }
    seen.add(key);
    const registration = registrationById.get(id(participant?.registrationId || score.registrationId));
    const person = { ...profile(registration), ...profile(participant) };
    const issues = [];
    if (!participant) issues.push("Roster identity not linked");
    const nraId = text(person.nraMemberId || membership(person, "nra") || person.bullseyeNraCompetitorId || membership(person, "nra-competitor"));
    if (!nraId) issues.push("NRA ID missing");
    const address = [person.streetAddress1, person.streetAddress2,
      [person.city, [person.state, person.zipCode].map(text).filter(Boolean).join(" ")].map(text).filter(Boolean).join(", ")]
      .map(text).filter(Boolean).join("\n");
    if (![person.streetAddress1, person.city, person.state, person.zipCode].every(value => text(value))) issues.push("Address incomplete");
    if (!text(person.lastName)) issues.push("Reporting name incomplete");
    const equipment = participation(person, score);
    if (!equipment) issues.push("Division not provided");
    if (number(score.plannedShots) > shots) issues.push(`Partial score: ${shots} of ${score.plannedShots} shots`);
    if (!/^(official|confirmed|final)$/i.test(score.status || "")) issues.push("Score not final");
    if (score.outcomeStatus && !/^(active|normal)$/i.test(score.outcomeStatus)) issues.push(`Outcome: ${score.outcomeStatus}`);
    rows.push({ key, nraId, shotsFired: shots, totalScore: total, xCount: xs,
      name: reportName(person, name), address, equipment, issues });
  }
  rows.sort((a, b) => a.name.localeCompare(b.name, "en", { sensitivity: "base", numeric: true }) || a.key.localeCompare(b.key));
  return { rows, errors, unscored, reviewCount: rows.filter(row => row.issues.length).length };
}
